import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  AuthResponse,
  ImpersonationResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UserProfileApiResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ConfirmResetPasswordRequest,
  ChangePasswordRequest,
  AllUsersResponse,
} from '../types/auth.types';

// Define a type for the resend email verification response data
interface ResendEmailVerificationResponseData {
  verificationCode: string;
  expiresAt: string;
}

// Define a type for the email verification request data
interface VerifyEmailRequest {
  email: string;
  code: string;
}

class AuthService {
  private readonly AUTH_BASE_URL = '/auth';
  private readonly USERS_BASE_URL = '/users';

  // Генерація або отримання session ID
  private getOrCreateSessionId(): string {
    let sessionId = localStorage.getItem('auth_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('auth_session_id', sessionId);
    }
    return sessionId;
  }

  private getStorageKey(key: string): string {
    const sessionId = this.getOrCreateSessionId();
    return `${sessionId}_${key}`;
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // Очищаємо попередню сесію при новому логіні
    this.clearTokens();
    this.clearOriginalTokens();
    localStorage.removeItem('auth_session_id');

    const response = await api.post<ApiResponse<AuthResponse>>(
      `${this.AUTH_BASE_URL}/login`,
      credentials,
    );
    const authData = unwrapApiResponse<AuthResponse>(response);
    this.saveTokens(authData.accessToken, authData.refreshToken);
    return authData;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>(
      `${this.AUTH_BASE_URL}/register`,
      userData,
    );
    const authData = unwrapApiResponse<AuthResponse>(response);
    this.saveTokens(authData.accessToken, authData.refreshToken);
    return authData;
  }

  async logout(): Promise<void> {
    try {
      await api.post(`${this.AUTH_BASE_URL}/logout`);
    } finally {
      this.clearAllAuthData(); // Викликаємо новий метод для повного очищення
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('Немає токена оновлення');
    }
    const response = await api.post<ApiResponse<AuthResponse>>(
      `${this.AUTH_BASE_URL}/refresh`,
      { refreshToken },
    );
    const authData = unwrapApiResponse<AuthResponse>(response);
    this.saveTokens(authData.accessToken, authData.refreshToken);
    return authData;
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get<UserProfileApiResponse>(`${this.USERS_BASE_URL}/me`);
    const apiData = unwrapApiResponse<UserProfileApiResponse['data']>(response);
    return {
      id: apiData.system.id,
      email: apiData.general.email,
      name: apiData.general.name,
      surname: apiData.general.surname || undefined,
      patronymic: apiData.general.patronymic || undefined,
      phone: apiData.general.phone || undefined,
      roles: apiData.system.roles,
      permissions: apiData.system.permissions,
      isEmailVerified: apiData.system.isEmailVerified,
      isFirstSurveyCompleted: apiData.system.isFirstSurveyCompleted, // Додано нове поле
      lastLoginAt: apiData.system.lastLoginAt,
      createdAt: apiData.system.createdAt,
      avatar: apiData.general.avatar || undefined,
      isActive: apiData.system.isActive,
      professional: apiData.professional,
      measurements: apiData.measurements,
    };
  }

  async getAllUsers(params: { page?: number; limit?: number; search?: string } = {}): Promise<AllUsersResponse> {
    const response = await api.get<ApiResponse<AllUsersResponse>>(`${this.USERS_BASE_URL}/all`, { params });
    return unwrapApiResponse<AllUsersResponse>(response);
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await api.put(`${this.AUTH_BASE_URL}/change-password`, data);
  }

  // --- Методи імперсонації ---

  async impersonate(userId: number): Promise<ImpersonationResponse> {
    const response = await api.post<any>(`/admin/users/${userId}/impersonate`);
    const data: ImpersonationResponse = response.data.data;
    sessionStorage.setItem('impersonationToken', data.accessToken);
    sessionStorage.setItem('impersonationSessionId', String(data.sessionId));
    sessionStorage.setItem('impersonationTarget', JSON.stringify(data.targetUser));
    return data;
  }

  clearImpersonationData(): void {
    sessionStorage.removeItem('impersonationToken');
    sessionStorage.removeItem('impersonationSessionId');
    sessionStorage.removeItem('impersonationTarget');
    sessionStorage.removeItem('impersonationAdminUser');
  }

  isImpersonating(): boolean {
    return !!sessionStorage.getItem('impersonationToken');
  }

  getImpersonationSessionId(): number | null {
    const raw = sessionStorage.getItem('impersonationSessionId');
    return raw ? Number(raw) : null;
  }

  async revokeImpersonationSession(sessionId: number): Promise<void> {
    await api.delete(`/admin/impersonation-sessions/${sessionId}`);
  }

  // Залишаємо для зворотної сумісності (старий код checkAuth)
  saveOriginalTokens(): void {}
  restoreOriginalTokens(): void { this.clearImpersonationData(); }
  clearOriginalTokens(): void { this.clearImpersonationData(); }

  // --- Управління токенами ---
  saveTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem(this.getStorageKey('access_token'), accessToken);
    if (refreshToken) localStorage.setItem(this.getStorageKey('refresh_token'), refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(this.getStorageKey('access_token'));
    localStorage.removeItem(this.getStorageKey('refresh_token'));
  }

  // Новий метод для повного очищення всіх даних автентифікації
  clearAllAuthData(): void {
    this.clearTokens(); // Очищаємо основні токени
    this.clearOriginalTokens(); // Очищаємо токени імперсонації
    localStorage.removeItem('auth_session_id'); // Видаляємо ID сесії
    
    // Додатково очищаємо весь sessionStorage та localStorage для максимальної впевненості
    // Зберігаємо UI-налаштування які не пов'язані з авторизацією
    const preserve: Record<string, string | null> = {
      'ui-variant':        localStorage.getItem('ui-variant'),
      'color-mode':        localStorage.getItem('color-mode'),
      'font-scale':        localStorage.getItem('font-scale'),
    };
    sessionStorage.clear();
    localStorage.clear();
    Object.entries(preserve).forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
  }

  getAccessToken(): string | null {
    // Impersonation token takes priority when active
    return sessionStorage.getItem('impersonationToken') ?? localStorage.getItem(this.getStorageKey('access_token'));
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.getStorageKey('refresh_token'));
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // --- Скидання пароля ---
  async requestPasswordReset(email: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await api.post<ApiResponse<ResetPasswordResponse>>(`${this.AUTH_BASE_URL}/password-reset/request`, email);
    return unwrapApiResponse<ResetPasswordResponse>(response);
  }

  async confirmPasswordReset(data: ConfirmResetPasswordRequest): Promise<void> {
    await api.post(`${this.AUTH_BASE_URL}/password-reset/confirm-code`, data);
  }

  async resendEmailVerificationCode(email: string): Promise<ResendEmailVerificationResponseData> {
    const response = await api.post<ApiResponse<ResendEmailVerificationResponseData>>(
      `${this.AUTH_BASE_URL}/email/resend`,
      { email },
    );
    return unwrapApiResponse<ResendEmailVerificationResponseData>(response);
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<void> {
    const response = await api.post<ApiResponse<any>>(
      `${this.AUTH_BASE_URL}/email/verify`,
      data,
    );
    unwrapApiResponse<any>(response); // We don't care about the data, just success
  }
}

export const authService = new AuthService();
